import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { publicApi } from '@/lib/api';
import { analyticsConsent } from '@/lib/consent';

// Generate anonymous session ID
const getSessionId = () => {
    let sessionId = sessionStorage.getItem('analytics_session');
    if (!sessionId) {
        sessionId = 'sess_' + Math.random().toString(36).substring(2, 15);
        sessionStorage.setItem('analytics_session', sessionId);
    }
    return sessionId;
};

// Sanitize URL - remove PII from query strings
const sanitizeUrl = (url) => {
    try {
        const urlObj = new URL(url, window.location.origin);
        const params = urlObj.searchParams;
        
        // List of params to sanitize (remove completely)
        const piiParams = ['email', 'name', 'phone', 'address', 'token', 'password', 'key', 'secret'];
        
        piiParams.forEach(param => {
            if (params.has(param)) {
                params.delete(param);
            }
        });
        
        // Keep UTM params for campaign tracking
        const allowedParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ref'];
        const newParams = new URLSearchParams();
        
        allowedParams.forEach(param => {
            if (params.has(param)) {
                newParams.set(param, params.get(param));
            }
        });
        
        urlObj.search = newParams.toString();
        return urlObj.pathname + (urlObj.search ? urlObj.search : '');
    } catch (e) {
        return url;
    }
};

// Get device info
const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    let device = 'desktop';
    
    if (/mobile/i.test(ua)) {
        device = 'mobile';
    } else if (/tablet|ipad/i.test(ua)) {
        device = 'tablet';
    }
    
    let browser = 'other';
    if (/chrome/i.test(ua) && !/edge/i.test(ua)) {
        browser = 'chrome';
    } else if (/firefox/i.test(ua)) {
        browser = 'firefox';
    } else if (/safari/i.test(ua) && !/chrome/i.test(ua)) {
        browser = 'safari';
    } else if (/edge/i.test(ua)) {
        browser = 'edge';
    }
    
    return { device, browser };
};

// Track pageview
export const trackPageview = async (path) => {
    const consent = analyticsConsent();
    if (!consent) {
        console.log('[Analytics] No analytics consent - skipping pageview');
        return;
    }

    try {
        const { device, browser } = getDeviceInfo();
        const referrer = document.referrer ? new URL(document.referrer).hostname : '';
        
        await publicApi.trackEvent({
            type: 'pageview',
            path: sanitizeUrl(path || window.location.pathname),
            session_id: getSessionId(),
            referrer: referrer,
            device,
            browser,
            screen_width: window.innerWidth,
            utm_source: new URLSearchParams(window.location.search).get('utm_source') || '',
            utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || '',
            utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
            consent_id: consent.consent_id,
            policy_version: consent.version,
        });
        console.log('[Analytics] Pageview tracked:', sanitizeUrl(path || window.location.pathname));
    } catch (e) {
        console.error('[Analytics] Error tracking pageview:', e);
    }
};

// Track custom event
export const trackEvent = async (eventName, eventData = {}) => {
    const consent = analyticsConsent();
    if (!consent) {
        console.log('[Analytics] No analytics consent - skipping event:', eventName);
        return;
    }

    try {
        await publicApi.trackEvent({
            type: 'event',
            event_name: eventName,
            event_data: eventData,
            path: sanitizeUrl(window.location.pathname),
            session_id: getSessionId(),
            consent_id: consent.consent_id,
            policy_version: consent.version,
        });
        console.log('[Analytics] Event tracked:', eventName, eventData);
    } catch (e) {
        console.error('[Analytics] Error tracking event:', e);
    }
};

// Predefined events
export const trackCTAClick = (ctaName, ctaUrl) => {
    trackEvent('cta_click', { cta_name: ctaName, cta_url: ctaUrl });
};

export const trackSoftwareView = (softwareId, softwareName) => {
    trackEvent('software_view', { software_id: softwareId, software_name: softwareName });
};

export const trackDownloadClick = (downloadId, softwareName, version) => {
    trackEvent('download_click', { download_id: downloadId, software_name: softwareName, version });
};

export const trackFormSubmit = (formName) => {
    trackEvent('form_submit', { form_name: formName });
};

// React Hook for automatic pageview tracking
export const useAnalytics = () => {
    const { pathname, search } = useLocation();

    useEffect(() => {
        trackPageview(pathname + search);
    }, [pathname, search]);

    useEffect(() => {
        // Listen for consent updates
        const handleConsentUpdate = (e) => {
            if (e.detail?.analytics) {
                trackPageview();
            }
        };
        window.addEventListener('consentUpdated', handleConsentUpdate);

        return () => {
            window.removeEventListener('consentUpdated', handleConsentUpdate);
        };
    }, []);

    return {
        trackEvent,
        trackCTAClick,
        trackSoftwareView,
        trackDownloadClick,
        trackFormSubmit,
    };
};

export default { trackPageview, trackEvent, useAnalytics };
